import {
  AnchorBlock,
  BaseLandingPageBlock,
  ContentListCuratedBlock,
  ContentListSplitBlock,
  ContentListTabbedBlock,
  DownloadableAssetsBlock,
  FeatureListBlock,
  HeroFullWidth,
  HeroSplitWithImage,
  HubSpotFormEmbedBlock,
  ImageBlock,
  InvalidProperties,
  LandingPageBlockV2,
  LandingPageV2BlockTypes,
  LandingPageV2Hero,
  LeadGenerationFormBlock,
  SearchBlock,
  TabbedNavigationBlock,
  UnknownBlockType,
  WysiwygBlock,
  YouTubeEmbedBlock,
} from "./schemas";

const isLandingPageBlockWithFieldName = <T extends BaseLandingPageBlock>(
  block: LandingPageBlockV2,
  fieldName: T["acfFcLayout"]
): boolean =>
  block &&
  typeof block === "object" &&
  "acfFcLayout" in block &&
  block.acfFcLayout === fieldName;

export const isAnchorBlock = (
  block: LandingPageBlockV2
): block is AnchorBlock =>
  isLandingPageBlockWithFieldName<AnchorBlock>(
    block,
    LandingPageV2BlockTypes.Anchor
  );

export const isContentListCuratedBlock = (
  block: LandingPageBlockV2
): block is ContentListCuratedBlock =>
  isLandingPageBlockWithFieldName<ContentListCuratedBlock>(
    block,
    LandingPageV2BlockTypes.ContentListCurated
  );

export const isContentListSplitBlock = (
  block: LandingPageBlockV2
): block is ContentListSplitBlock =>
  isLandingPageBlockWithFieldName<ContentListSplitBlock>(
    block,
    LandingPageV2BlockTypes.ContentListSplit
  );

export const isContentListTabbedBlock = (
  block: LandingPageBlockV2
): block is ContentListTabbedBlock =>
  isLandingPageBlockWithFieldName<ContentListTabbedBlock>(
    block,
    LandingPageV2BlockTypes.ContentListTabbed
  );

export const isDownloadableAssetsBlock = (
  block: LandingPageBlockV2
): block is DownloadableAssetsBlock =>
  isLandingPageBlockWithFieldName<DownloadableAssetsBlock>(
    block,
    LandingPageV2BlockTypes.Downloads
  );

export const isUnknownBlockType = (
  block: LandingPageBlockV2
): block is UnknownBlockType =>
  isLandingPageBlockWithFieldName<UnknownBlockType>(
    block,
    LandingPageV2BlockTypes.UnknownBlockType
  );

export const isInvalidPropertiesError = (
  block: LandingPageBlockV2
): block is InvalidProperties =>
  isLandingPageBlockWithFieldName<InvalidProperties>(
    block,
    LandingPageV2BlockTypes.InvalidProperties
  );

export const isHeroFullWidth = (
  hero: LandingPageV2Hero
): hero is HeroFullWidth => hero.appearance === "full_width";

export const isHeroSplitWithImage = (
  hero: LandingPageV2Hero
): hero is HeroSplitWithImage => hero.appearance === "split_with_image";

export const isFeatureListBlock = (
  block: LandingPageBlockV2
): block is FeatureListBlock =>
  isLandingPageBlockWithFieldName<FeatureListBlock>(
    block,
    LandingPageV2BlockTypes.FeatureList
  );

export const isImageBlock = (block: LandingPageBlockV2): block is ImageBlock =>
  isLandingPageBlockWithFieldName<ImageBlock>(
    block,
    LandingPageV2BlockTypes.Image
  );

export const isLeadGenerationBlock = (
  block: LandingPageBlockV2
): block is LeadGenerationFormBlock =>
  isLandingPageBlockWithFieldName<LeadGenerationFormBlock>(
    block,
    LandingPageV2BlockTypes.LeadGenerationForm
  );

export const isSearchBlock = (
  block: LandingPageBlockV2
): block is SearchBlock =>
  isLandingPageBlockWithFieldName<SearchBlock>(
    block,
    LandingPageV2BlockTypes.Search
  );

export const isTabbedNavigationBlock = (
  block: LandingPageBlockV2
): block is TabbedNavigationBlock =>
  isLandingPageBlockWithFieldName<TabbedNavigationBlock>(
    block,
    LandingPageV2BlockTypes.TabbedNavigation
  );

export const isWysiwygBlock = (
  block: LandingPageBlockV2
): block is WysiwygBlock =>
  isLandingPageBlockWithFieldName<WysiwygBlock>(
    block,
    LandingPageV2BlockTypes.Wysiwyg
  );

export const isYoutubeEmbedBlock = (
  block: LandingPageBlockV2
): block is YouTubeEmbedBlock =>
  isLandingPageBlockWithFieldName<YouTubeEmbedBlock>(
    block,
    LandingPageV2BlockTypes.YoutubeEmbed
  );

export const isHubSpotFormEmbedBlock = (
  block: LandingPageBlockV2
): block is HubSpotFormEmbedBlock =>
  isLandingPageBlockWithFieldName<HubSpotFormEmbedBlock>(
    block,
    LandingPageV2BlockTypes.HubSpotForm
  );
